import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { LOGO_URL, COMPANY } from "@/lib/api";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div data-testid="top-utility-bar" className="bg-[#111827] text-white text-xs sm:text-sm py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center gap-4">
          <div className="flex items-center gap-5 ml-auto">
            <a
              href={`mailto:${COMPANY.email}`}
              data-testid="utility-email"
              className="flex items-center gap-1.5 hover:text-[#FFC107] transition-colors"
            >
              <Mail className="h-3.5 w-3.5" />
              <span>{COMPANY.email}</span>
            </a>
            <a
              href={`tel:${COMPANY.phone}`}
              data-testid="utility-phone"
              className="flex items-center gap-1.5 hover:text-[#FFC107] transition-colors"
            >
              <Phone className="h-3.5 w-3.5" />
              <span>{COMPANY.phoneDisplay}</span>
            </a>
          </div>
        </div>
      </div>

      <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-18 py-3">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt={`${COMPANY.name} logo`}
              width="44"
              height="44"
              decoding="async"
              className="h-11 w-11 object-contain"
            />
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-gray-900">Gold Mind</div>
              <div className="text-[0.6rem] tracking-[0.3em] uppercase font-accent font-bold text-[#B01116]">
                Business
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-semibold transition-colors relative group ${
                    isActive ? "text-[#B01116]" : "text-gray-700 hover:text-[#B01116]"
                  }`
                }
              >
                {n.label}
                <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-[#B01116] transition-all duration-300 group-hover:w-full" />
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="border-2 border-[#B01116] text-[#B01116] hover:bg-[#B01116] hover:text-white px-6 py-2 rounded-full font-accent font-bold text-sm transition-colors inline-flex items-center gap-2"
            >
              Request Callback
              <Phone className="h-4 w-4" />
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-gray-900 p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div data-testid="nav-mobile-menu" className="md:hidden border-t border-gray-100 bg-white">
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `text-base font-semibold transition-colors py-1 ${
                      isActive ? "text-[#B01116]" : "text-gray-800 hover:text-[#B01116]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-cta"
                className="border-2 border-[#B01116] text-[#B01116] px-6 py-2.5 rounded-full font-accent font-bold text-sm text-center mt-2"
              >
                Request Callback
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
